import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Slider,
  Stack,
  Switch,
  Box,
  Typography,
  Collapse,
  Alert,
  Link,
} from '@mui/material';
import {
  Controller,
  DefaultValues,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { RRule, Frequency } from 'rrule';
import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { add, addHours, startOfDay, startOfHour } from 'date-fns';
import format from 'date-fns/format';
import { FormattedMessage } from 'react-intl';

import { Select } from '../../atoms/form/form-select-field';
import { FormTextField } from '../../atoms/form/form-text-field';
import { FormDatetimeSelectPicker } from '../../atoms/form/form-datetime-select-field';
import { trpc } from '../../api/trpc';
import { DialogClose } from '../../atoms/dialog-close';
import { FormattedDateTime } from '../../utils/formatted-datetime';
import { sliderSx } from '../../molecules/slider-box';
import { useFormatNumber } from '../../utils/format-number';
import {
  chargingCurrentMax,
  chargingCurrentMin,
  ScheduleType,
  scheduleTypes,
} from './schedules.types';

/** Formats a duration in minutes as "HH:mm". */
const formatDuration = (minutes: number) =>
  format(add(startOfDay(new Date()), { minutes }), 'HH:mm');

export const preprocessFloatNullable = (schema: z.ZodNullable<z.ZodNumber>) =>
  z.preprocess(
    (val) => (val ? parseFloat(String(val).replace(',', '')) : null),
    schema
  );

const payload = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('battery-charge'),
    targetSoc: preprocessFloatNullable(
      z
        .number({
          invalid_type_error: 'Musí být číslo',
          required_error: 'Povinné pole',
        })
        .min(10, { message: 'Minimální hodnota je 10%' })
        .max(100, { message: 'Maximální hodnota je 100%' })
        .nullable()
    ),
  }),
  z.object({
    type: z.literal('battery-discharge'),
    targetSoc: preprocessFloatNullable(
      z
        .number({
          invalid_type_error: 'Musí být číslo',
          required_error: 'Povinné pole',
        })
        .min(10, { message: 'Minimální hodnota je 10%' })
        .max(100, { message: 'Maximální hodnota je 100%' })
        .nullable()
    ),
  }),
  z.object({
    type: z.literal('ev-charging'),
    chargingCurrent: preprocessFloatNullable(
      z
        .number({
          invalid_type_error: 'Musí být číslo',
          required_error: 'Povinné pole',
        })
        .min(chargingCurrentMin, {
          message: `Minimální hodnota je ${chargingCurrentMin} A`,
        })
        .max(chargingCurrentMax, {
          message: `Maximální hodnota je ${chargingCurrentMax} A`,
        })
        .nullable()
    ),
  }),
]);

const baseSchema = z.object({
  startAt: z.date({ invalid_type_error: 'Neplatné datum' }),
  duration: z.number().min(1).optional(), // minutes
  recurring: z
    .object({
      frequency: z.number().min(0).max(3),
      interval: z.number().min(1).max(2).optional(),
      until: z.date().optional().nullable(),
    })
    .or(z.literal(false)),
  rruleString: z.string().optional(),
});

const schema = baseSchema.and(payload);

type Schema = z.infer<typeof schema>;

const defaultRecurring = {
  frequency: Frequency.WEEKLY,
  interval: 1,
  until: null,
};

/** Pre-filled when the user opts into a current; same default as the wallbox. */
const defaultChargingCurrent = 16;

const getDefaultValues = (
  types: readonly ScheduleType[]
): DefaultValues<Schema> => ({
  type: types[0],
  targetSoc: null,
  startAt: startOfHour(addHours(new Date(), 1)),
  duration: 180,
  recurring: false,
});

const typeLabels: Record<ScheduleType, React.ReactNode> = {
  'battery-charge': (
    <FormattedMessage
      id="schedules.form.battery_charge"
      defaultMessage="Nabítí baterie"
    />
  ),
  'battery-discharge': (
    <FormattedMessage
      id="schedules.form.battery_discharge"
      defaultMessage="Vybití baterie"
    />
  ),
  'ev-charging': (
    <FormattedMessage
      id="schedules.form.ev_charging"
      defaultMessage="Nabíjení elektromobilu"
    />
  ),
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Actions offered in the dialog. Defaults to all of them. */
  types?: readonly ScheduleType[];
};

export const ScheduleForm = ({
  open,
  onClose,
  types = scheduleTypes,
}: Props) => {
  const formatNumber = useFormatNumber();
  const utils = trpc.useContext();
  const { mutate, isLoading } = trpc.schedules.create.useMutation({
    onSuccess() {
      onClose();
      utils.schedules.list.invalidate();
    },
    onError() {
      alert('Chyba');
    },
  });

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(types),
  });

  useEffect(() => {
    form.reset(getDefaultValues(types));
  }, [open]);

  const type = form.watch('type');
  const recurring = form.watch('recurring');

  // The photovoltaics page reuses this dialog for battery plans, so the
  // charging wording only applies when EV charging is the only action.
  const evOnly = types.length === 1 && types[0] === 'ev-charging';

  const onSubmit = (data: Schema) => {
    mutate(data as Schema);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogClose onClose={onClose} />

        <DialogTitle variant="h2">
          {evOnly ? (
            <FormattedMessage
              id="schedules.dialog.title.ev"
              defaultMessage="Vytvořit plán nabíjení"
            />
          ) : (
            <FormattedMessage
              id="schedules.dialog.title"
              defaultMessage="Vytvořit plán"
            />
          )}
        </DialogTitle>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogContent>
              <Stack gap={2}>
                {types.length > 1 && (
                  <Select
                    name="type"
                    label={
                      <FormattedMessage
                        id="schedules.form.action"
                        defaultMessage="Akce"
                      />
                    }
                  >
                    {types.map((value) => (
                      <MenuItem key={value} value={value}>
                        {typeLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                )}

                {['battery-charge', 'battery-discharge'].includes(type) && (
                  <FormTextField
                    name="targetSoc"
                    label={
                      <FormattedMessage
                        id="schedules.form.target-soc"
                        defaultMessage="Cilové SOC (volitelné)"
                      />
                    }
                    size="small"
                  />
                )}

                {type === 'ev-charging' && (
                  <Controller
                    name="chargingCurrent"
                    render={({ field }) => {
                      // Null means "leave the current to the active wallbox
                      // mode"; the slider only appears once opted in.
                      const enabled =
                        field.value !== null && field.value !== undefined;

                      return (
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: enabled ? 1 : 0 }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={enabled}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.checked
                                        ? defaultChargingCurrent
                                        : null
                                    )
                                  }
                                />
                              }
                              label={
                                <Typography variant="h3">
                                  <FormattedMessage
                                    id="schedules.form.charging-current"
                                    defaultMessage="Nastavit nabíjecí proud"
                                  />
                                </Typography>
                              }
                              sx={{ ml: -1.25, mr: 0 }}
                            />
                            <Typography variant="h3">
                              {enabled ? (
                                formatNumber(Number(field.value), {
                                  style: 'unit',
                                  unit: 'ampere',
                                  maximumFractionDigits: 0,
                                })
                              ) : (
                                <FormattedMessage
                                  id="schedules.form.charging-current.auto"
                                  defaultMessage="Výchozí"
                                />
                              )}
                            </Typography>
                          </Stack>
                          {enabled && (
                            <Box sx={{ px: 1 }}>
                              <Slider
                                value={Number(field.value)}
                                onChange={(_, value) => field.onChange(value)}
                                min={chargingCurrentMin}
                                max={chargingCurrentMax}
                                step={1}
                                sx={sliderSx}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    }}
                  />
                )}

                <FormDatetimeSelectPicker
                  name="startAt"
                  size="small"
                  label={
                    <FormattedMessage
                      id="schedules.form.when"
                      defaultMessage="Kdy"
                    />
                  }
                />

                <Controller
                  name="duration"
                  render={({ field }) => (
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="h3">
                          <FormattedMessage
                            id="schedules.form.duration"
                            defaultMessage="Délka"
                          />
                        </Typography>
                        <Typography variant="h3">
                          {formatDuration(field.value ?? 0)}
                        </Typography>
                      </Stack>
                      <Box sx={{ px: 1 }}>
                        <Slider
                          value={field.value ?? 15}
                          onChange={(_, value) => field.onChange(value)}
                          min={15}
                          max={360}
                          step={15}
                          sx={sliderSx}
                        />
                      </Box>
                    </Box>
                  )}
                />

                <Box>
                  <Controller
                    name="recurring"
                    render={({ field: { value } }) => (
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            checked={Boolean(value)}
                            onChange={(e) => {
                              form.setValue(
                                'recurring',
                                e.target.checked ? defaultRecurring : false
                              );
                            }}
                            sx={{ mr: 0.75 }}
                          />
                        }
                        label={
                          <FormattedMessage
                            id="schedules.form.recurring"
                            defaultMessage="Opakující se"
                          />
                        }
                      />
                    )}
                  />
                </Box>

                {recurring && <Recurring />}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
              >
                <FormattedMessage
                  id="schedules.form.create"
                  defaultMessage="Vytvořit"
                />
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};

const PREVIEW_COUNT = 10;

/**
 * The server expands recurring rules on the wall clock, not on UTC, so that a
 * 10:00 series stays at 10:00 across a DST change. The preview has to use the
 * same two frames or it would advertise times the device will not run - see the
 * time-frame note in `schedules.repository.ts`.
 */
const toFloating = (date: Date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    )
  );

const fromFloating = (date: Date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes()
  );

const Recurring = () => {
  const { watch } = useFormContext();
  const [showPreview, setShowPreview] = useState(false);
  const startAt = watch('startAt') as Date;
  const recurring = watch('recurring');

  const getOccurrences = () => {
    if (!recurring || recurring === false) return [];

    try {
      const rule = new RRule({
        freq: recurring.frequency,
        dtstart: toFloating(startAt),
        interval: recurring.interval || 1,
        until: recurring.until ? toFloating(recurring.until) : undefined,
        count: recurring.until ? undefined : PREVIEW_COUNT,
      });

      return rule.all((_, i) => i < PREVIEW_COUNT).map(fromFloating);
    } catch (error) {
      console.error('Error generating occurrences:', error);
      return [];
    }
  };

  const occurrences = showPreview ? getOccurrences() : [];

  return (
    <>
      <Select
        sx={{ mt: 1 }}
        name="recurring.frequency"
        label={
          <FormattedMessage
            id="schedules.form.frequency"
            defaultMessage="Frekvence"
          />
        }
      >
        <MenuItem value={Frequency.DAILY}>
          <FormattedMessage
            id="schedules.form.frequency.daily"
            defaultMessage="Denně"
          />
        </MenuItem>
        <MenuItem value={Frequency.WEEKLY}>
          <FormattedMessage
            id="schedules.form.frequency.weekly"
            defaultMessage="Týdně"
          />
        </MenuItem>
        <MenuItem value={Frequency.MONTHLY}>
          <FormattedMessage
            id="schedules.form.frequency.monthly"
            defaultMessage="Měsíčně"
          />
        </MenuItem>
      </Select>

      {recurring.frequency === Frequency.WEEKLY && (
        <Controller
          name="recurring.interval"
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  checked={value === 2}
                  onChange={(e) => onChange(e.target.checked ? 2 : 1)}
                  sx={{ mr: 0.75 }}
                />
              }
              label={
                <FormattedMessage
                  id="schedules.form.interval.biweekly"
                  defaultMessage="Každé 2 týdny"
                />
              }
            />
          )}
        />
      )}

      <FormDatetimeSelectPicker
        name="recurring.until"
        size="small"
        label={
          <FormattedMessage
            id="schedules.form.until"
            defaultMessage="Opakovat do (volitelné)"
          />
        }
      />

      <Collapse in={showPreview}>
        {occurrences.length > 0 && (
          <Box sx={{}}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              <FormattedMessage
                id="schedules.form.preview.title"
                defaultMessage="Plánovaná spuštění ({count} následujících)"
                values={{ count: PREVIEW_COUNT }}
              />
            </Typography>
            <Alert severity="warning" icon={false}>
              {occurrences.map((date, index) => (
                <React.Fragment key={index}>
                  <FormattedDateTime value={date} />
                  <br />
                </React.Fragment>
              ))}
            </Alert>
          </Box>
        )}
      </Collapse>

      <Box sx={{ mt: 0.25, textAlign: 'center' }}>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setShowPreview(!showPreview);
          }}
        >
          {showPreview ? (
            <FormattedMessage
              id="schedules.form.preview.hide"
              defaultMessage="Skrýt náhled"
            />
          ) : (
            <FormattedMessage
              id="schedules.form.preview.show"
              defaultMessage="Zobrazit plánované výskyty"
            />
          )}
        </Link>
      </Box>
    </>
  );
};
